/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { saveMany } from '../../store/items';
import {
  getAvatarBackgroundStyle,
  getId,
  getItemPicAsset,
} from '../../utils/item';
import { canDisplayNavigationItem } from '../../utils/permissions';
import { NavigationSection } from './navigation.Section';
import { NavigationDisplayItems } from './navigation.displayItems';
import { useParams } from 'react-router';
import { setDrawerOpen } from '../../store/global';
import { renderIconBySlug } from '../../content/icons';
import { setChildren } from '../../store/navigation';

const isNavigationListItem = (item, parentType) => {
  if (item?.type === 'pic' || item?.type === 'tab') {
    return false;
  }
  // In folder view, ignore folder-typed children in the content list.
  if (parentType === 'campaign' && item?.type === 'campaign') {
    return false;
  }
  return true;
};
const isValidObjectId = id => typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id);

export const NavigationView = props => {
  const {
    viewId,
    parentId,
    itemsInSection,
    renderedSections,
    setRenderedSections,
  } = props;

  const view = useSelector(state => state.items[viewId]);
  const childrenByParent = useSelector(
    state => state.navigation.childrenByParent
  );
  const loadedParents = useSelector(state => state.navigation.loadedParents);
  const parent = useSelector(state => state.items[parentId]);
  const allItems = useSelector(state => state.items);
  const liveCampaignChildIds = useSelector(
    state => {
      if (!parentId) return [];
      const parentItem = state.items?.[parentId];
      if (parentItem?.type !== 'campaign') {
        return [];
      }
      const ids = [];
      const entries = Object.entries(state.items || {});
      for (let i = 0; i < entries.length; i++) {
        const [id, candidate] = entries[i];
        if (!candidate || candidate?._id !== id) continue;
        if (
          candidate?.campaign === parentId &&
          candidate?.type !== 'tab' &&
          candidate?.type !== 'campaign'
        ) {
          ids.push(id);
        }
      }
      ids.sort();
      return ids;
    },
    shallowEqual
  );

  const viewLinks = view?.links ?? [];
  const parentLinks = parent?.links ?? [];
  const cachedChildrenIds = parentId ? childrenByParent[parentId] || [] : [];
  const itemsInSectionIds = itemsInSection?.map(item => item?._id) || [];
  const trackedIds = useMemo(() => {
    const directIds = [
      ...viewLinks.map(link => getId(link)),
      ...parentLinks.map(link => getId(link)),
      ...cachedChildrenIds,
      ...itemsInSectionIds,
    ].filter(Boolean);
    const scopeIds = directIds.flatMap(id => {
      const item = allItems?.[id];
      if (!item) {
        return [];
      }
      return [item.campaign, item.world, item.universe].filter(isValidObjectId);
    });
    const ids = [
      viewId,
      parentId,
      ...directIds,
      ...scopeIds,
    ].filter(Boolean);
    return Array.from(new Set(ids));
  }, [
    allItems,
    viewId,
    parentId,
    viewLinks,
    parentLinks,
    cachedChildrenIds,
    itemsInSectionIds,
  ]);
  const itemsById = useSelector(state => {
    const mapped = {};
    trackedIds.forEach(id => {
      if (state.items[id]) {
        mapped[id] = state.items[id];
      }
    });
    return mapped;
  }, shallowEqual);

  const dispatch = useDispatch();

  const [viewIdLinks, setViewIdLinks] = useState([]);
  const [noLinksInSection, setNoLinksInSection] = useState([]);
  const [list, setListInParent] = useState([]);
  const [imageList, setImageList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const isFetchingParentRef = useRef(false);
  const requestedMissingIdsRef = useRef(new Set());
  const [visibleListCount, setVisibleListCount] = useState(80);
  const [visibleImageCount, setVisibleImageCount] = useState(60);
  const safeNoLinks = Array.isArray(noLinksInSection) ? noLinksInSection : [];
  const safeImageList = Array.isArray(imageList) ? imageList : [];
  const canDisplayInNavigation = item => {
    if (!item?._id) {
      return false;
    }
    return canDisplayNavigationItem({
      item,
      campaign: itemsById?.[item?.campaign],
      world: itemsById?.[item?.world],
      universe: itemsById?.[item?.universe],
      items: itemsById,
    });
  };

  useEffect(() => {
    if (!trackedIds.length) {
      return;
    }
    const missingIds = trackedIds.filter(
      id => !itemsById[id] && !requestedMissingIdsRef.current.has(id)
    );
    if (missingIds.length === 0) {
      return;
    }
    missingIds.forEach(id => requestedMissingIdsRef.current.add(id));
    const fetchMissing = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/items/batch`,
          { ids: missingIds }
        );
        if (Array.isArray(res.data)) {
          dispatch(
            saveMany({ items: res.data, log: 'from navigationView batch' })
          );
        }
      } catch (error) {
        console.error('Failed to fetch navigation items:', error);
        // allow retry on next pass if request failed
        missingIds.forEach(id => requestedMissingIdsRef.current.delete(id));
      }
    };
    fetchMissing();
  }, [trackedIds, itemsById, dispatch]);

  const getParentList = async () => {
    if (isFetchingParentRef.current) {
      return;
    }
    isFetchingParentRef.current = true;
    setIsLoading(true);
    const type = parent?.type === 'pic' ? 'campaign' : parent?.type;
    const id = parent?._id;
    try {
      const allItems = [];
      let page = 1;
      let hasMore = true;
      const pageLimit = 30;
      while (hasMore) {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/items/${type}/${id}`,
          {
            params: { page, limit: pageLimit },
          }
        );
        const payload = res?.data;
        const pageItems = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
          ? payload.items
          : [];
        if (pageItems.length > 0) {
          allItems.push(...pageItems);
          const sortedItems = [...allItems].sort((a, b) => {
            if (a.label < b.label) {
              return -1;
            }
            if (a.label > b.label) {
              return 1;
            }
            return 0;
          });
          const filteredPics = sortedItems.filter(item => item?.type === 'pic');
          const remainingItems = sortedItems.filter(item =>
            isNavigationListItem(item, parent?.type),
          );
          setListInParent(remainingItems);
          setImageList(filteredPics);
          dispatch(
            saveMany({
              items: pageItems,
              log: 'from getParentList in navigationView.js',
            })
          );
        }
        if (Array.isArray(payload)) {
          hasMore = false;
        } else {
          hasMore = Boolean(payload?.hasMore);
        }
        page += 1;
      }

      if (allItems.length > 0) {
        //order res alphabetically
        allItems.sort((a, b) => {
          if (a.label < b.label) {
            return -1;
          }
          if (a.label > b.label) {
            return 1;
          }
          return 0;
        });
      }
      if (id) {
        dispatch(
          setChildren({
            parentId: id,
            childIds: allItems.map(child => child?._id).filter(Boolean),
          })
        );
      }

      const filteredPics = allItems.filter(item => item?.type === 'pic');
      const remainingItems = allItems.filter(item =>
        isNavigationListItem(item, parent?.type),
      );
      setListInParent(remainingItems);
      setImageList(filteredPics);
    } finally {
      isFetchingParentRef.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!itemsInSection && parent?._id) {
      const cachedChildren = childrenByParent[parent._id];
      if (loadedParents[parent._id] && Array.isArray(cachedChildren)) {
        const liveParentLinkIds = Array.isArray(parent?.links)
          ? parent.links.map(link => getId(link)).filter(Boolean)
          : [];
        const mergedChildIds = Array.from(
          new Set([...cachedChildren, ...liveParentLinkIds, ...liveCampaignChildIds])
        );

        if (
          mergedChildIds.length !== cachedChildren.length ||
          mergedChildIds.some(id => !cachedChildren.includes(id))
        ) {
          dispatch(
            setChildren({
              parentId: parent._id,
              childIds: mergedChildIds,
            })
          );
        }

        const cachedItems = mergedChildIds
          .map(id => itemsById[id])
          .filter(Boolean);
        const visibleCachedItems = cachedItems.filter(canDisplayInNavigation);
        const filteredPics = visibleCachedItems.filter(item => item?.type === 'pic');
        const remainingItems = visibleCachedItems.filter(item =>
          isNavigationListItem(item, parent?.type),
        );
        setListInParent(remainingItems);
        setImageList(filteredPics);
        setIsLoading(false);
        return;
      }
      if (parent?.type) {
        getParentList();
        return;
      }
    }

    if (parent?.links?.length > 0 && !itemsInSection) {
      let itemLinks = [];
      for (let i = 0; i < parent.links.length; i++) {
        const linkId = getId(parent.links[i]);
        const linkItem = itemsById?.[linkId];
        if (!linkItem) {
          continue;
        }
        if (canDisplayInNavigation(linkItem)) {
          itemLinks.push(linkItem);
        }
      }

      const filteredPics = itemLinks?.filter(item => item?.type === 'pic');
      const remainingItems = itemLinks?.filter(item =>
        isNavigationListItem(item, parent?.type),
      );
      setListInParent(remainingItems);
      setImageList(filteredPics);
      setIsLoading(false);
    } else {
      const visibleItemsInSection = itemsInSection?.filter(canDisplayInNavigation);
      const filteredPics = visibleItemsInSection?.filter(item => item?.type === 'pic');
      const remainingItems = visibleItemsInSection?.filter(item =>
        isNavigationListItem(item, parent?.type),
      );
      setListInParent(remainingItems);
      setImageList(filteredPics);
      setIsLoading(false);
    }
  }, [
    parent?._id,
    parent?.type,
    parent?.links?.length,
    itemsInSection,
    childrenByParent,
    loadedParents,
    liveCampaignChildIds,
    itemsById,
  ]);

  useEffect(() => {
    if (view?.links?.length > 0) {
      view.links.forEach(link => {
        const linkId = getId(link);
        if (!viewIdLinks?.includes(linkId) && itemsById[linkId] !== undefined) {
          setViewIdLinks([...viewIdLinks, linkId]);
        }
      });
    } else {
      setNoLinksInSection(list);
    }
  }, [view, list]);

  useEffect(() => {
    if (view !== undefined && list !== undefined) {
      const noLinks = list?.filter(item => {
        const ids = item?.links?.map(link => getId(link));
        if (ids?.length === 0) {
          return true;
        }
        const sectionsIds = view?.links?.map(link => getId(link));
        const found = sectionsIds?.some(r => ids?.includes(r));
        return !found;
      });
      setNoLinksInSection(noLinks);
    }
  }, [view, list]);

  useEffect(() => {
    setVisibleListCount(80);
  }, [safeNoLinks]);

  useEffect(() => {
    setVisibleImageCount(60);
  }, [safeImageList]);

  useEffect(() => {
    if (safeNoLinks.length <= visibleListCount) {
      return;
    }
    let isCancelled = false;
    const schedule = () => {
      if (isCancelled) {
        return;
      }
      setVisibleListCount(prev =>
        Math.min(prev + 80, safeNoLinks.length)
      );
    };
    const hasIdle = typeof window !== 'undefined' && window.requestIdleCallback;
    const handle = hasIdle
      ? window.requestIdleCallback(schedule, { timeout: 200 })
      : setTimeout(schedule, 16);
    return () => {
      isCancelled = true;
      if (hasIdle) {
        window.cancelIdleCallback(handle);
      } else {
        clearTimeout(handle);
      }
    };
  }, [safeNoLinks, visibleListCount]);

  useEffect(() => {
    if (safeImageList.length <= visibleImageCount) {
      return;
    }
    let isCancelled = false;
    const schedule = () => {
      if (isCancelled) {
        return;
      }
      setVisibleImageCount(prev => Math.min(prev + 60, safeImageList.length));
    };
    const hasIdle = typeof window !== 'undefined' && window.requestIdleCallback;
    const handle = hasIdle
      ? window.requestIdleCallback(schedule, { timeout: 200 })
      : setTimeout(schedule, 16);
    return () => {
      isCancelled = true;
      if (hasIdle) {
        window.cancelIdleCallback(handle);
      } else {
        clearTimeout(handle);
      }
    };
  }, [safeImageList, visibleImageCount]);

  return (
    <div>
      {view?.links?.map(link => (
        <NavigationSectionLink
          key={getId(link)}
          link={link}
          parentId={parentId}
          list={list}
          renderedSections={renderedSections}
          setRenderedSections={setRenderedSections}
          itemsById={itemsById}
          isSubLevel={props.isSubLevel}
          classNameHeader={props.classNameHeader}
          classNameTitle={props.classNameTitle}
          hideSection={props.hideSection}
        />
      ))}
      {view?.links?.length > 0 && noLinksInSection?.length > 0 && (
        <div className='border-t border-contrast border-opacity-25 mx-5'></div>
      )}
      {isLoading && (
        <div className='p-2 text-center text-contrast text-opacity-60'>
          Chargement...
        </div>
      )}
      {safeNoLinks.slice(0, visibleListCount).map(item => {
        if (item?._id) {
          return (
            <NavigationDisplayItems
              key={item?._id}
              displayItem={item}
            />
          );
        }
      })}
      <div
        className='flex flex-wrap p-1'
        style={{ gap: '2%' }}
      >
        {safeImageList.slice(0, visibleImageCount).map(item => {
          if (!item?._id) {
            return null;
          }
          const avatarAsset = getItemPicAsset(item, 0);
          const hasIcon = Boolean(avatarAsset.iconSlug);
          const useImage = Boolean(avatarAsset.url) && !hasIcon;
          const bgStyle = avatarAsset.bgColor
            ? getAvatarBackgroundStyle(avatarAsset.bgColor)
            : { backgroundColor: 'rgba(var(--main), 0.3)' };
          return (
            <div
              key={item._id}
              onClick={() => {
                dispatch(setDrawerOpen(item?._id));
              }}
              className='rounded shadow cursor-pointer flex items-center justify-center'
              style={{
                padding: '11.7%',
                marginBottom: '2%',
                ...(useImage
                  ? {
                      background: `url(${avatarAsset.url}) no-repeat center / cover`,
                      ...bgStyle,
                    }
                  : bgStyle),
              }}
            >
              {hasIcon &&
                renderIconBySlug(avatarAsset.iconSlug, {
                  size: 48,
                  color: 'var(--contrast)',
                })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const NavigationSectionLink = props => {
  const {
    link,
    parentId,
    list,
    renderedSections,
    setRenderedSections,
    itemsById,
    isSubLevel,
  } = props;
  const linkId = getId(link);
  const filteredList = list?.filter(item => {
    const ids = item?.links?.map(link => getId(link));
    if (ids?.includes(linkId)) {
      return true;
    }
    return false;
  });

  const params = useParams();
  if (filteredList?.length > 0) {
    const isSectionAlreadyDisplayed = renderedSections?.includes(linkId);
    return (
      <NavigationSection
        key={linkId}
        sectionId={linkId}
        section={itemsById[linkId]}
        itemsInSection={filteredList}
        parentId={parentId}
        classNameHeader={props.classNameHeader}
        classNameTitle={props.classNameTitle}
        setRenderedSections={setRenderedSections}
        renderedSections={renderedSections}
        isSubLevel={isSubLevel}
        hideSection={
          isSectionAlreadyDisplayed ||
          filteredList.length < 2 ||
          linkId === params.id
        }
      />
    );
  } else {
    return null;
  }
};
